import React, { useState } from 'react';
import {
    KatButton,
    KatLayout,
    KatProgress,
    KatAlert,
} from '@amzn/katal-react';

// Core hooks
import {
    getGSLADataForMultipleWarehouses,
    getGSLARecommendationsForMultipleWarehouses,
    useFCMetadata,
} from 'src/core/hooks/useGranularSLA';
import { useURLSiteParam } from 'src/core/hooks/UseURLSiteParam';
import { useUser } from 'src/utils/UserContext';
import { getValidWHTsForCountry } from 'src/types/WarehouseHandlingTier';

// Our new hierarchical table component
import { GSLAHierarchicalTableAdapter } from '../../components/hierarchicalTable/GSLAHierarchicalTableAdapter';

// Styles
import '../../styles/granularSLA/productionCapability.scss';

/**
 * ProductionCapabilityManagerWithHierarchy replaces the original ProductionCapabilityManager
 * with a new implementation that uses the hierarchical table to display GSLA data
 * including handling tiers.
 */
const ProductionCapabilityManagerWithHierarchy = () => {
    // User context for permissions
    const { user } = useUser();
    
    // Check if user has edit permissions
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true || user?.isDevAdmin === true;
    
    // State for view-only mode - default to false for users with permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(false);
    
    // State for success notification
    const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    
    // Get the site ID from URL
    const siteId = useURLSiteParam();
    
    // Fetch FC metadata for country-based WHT filtering
    const {
        data: fcMetadata,
        isLoading: isFCMetadataLoading,
    } = useFCMetadata(siteId || '');
    
    // Compute available tiers based on country code
    const availableTiers = getValidWHTsForCountry(fcMetadata?.countryCode);
    
    // Fetch GSLA data
    const { 
        data: gslaData, 
        isLoading: isGSLADataLoading,
        refetch: refetchGSLA
    } = getGSLADataForMultipleWarehouses(siteId ? [siteId] : [], !viewOnlyMode);
    
    // Fetch GSLA recommendations
    const { 
        data: gslaRecommendations, 
        isLoading: isGSLARecommendationsLoading,
        refetch: refetchRecommendations
    } = getGSLARecommendationsForMultipleWarehouses(
        siteId ? [siteId] : [], 
        { enabled: (user?.isAuthorizedForSLAOverrides || user?.isDevAdmin) }
    )

    // Handle successful save
    const handleSaveSuccess = (message?: string) => {
        // Refetch data to ensure we have the latest values
        refetchGSLA();
        
        // Show success notification
        setSuccessMessage(message || 'GSLA values published successfully!');
        setShowSuccessNotification(true);
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
            setShowSuccessNotification(false);
        }, 5000);
    };
    
    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };
    
    // Determine if user can edit based on permissions and view mode
    const userCanEdit = hasEditPermission && !viewOnlyMode;

    // Show loading state
    if (isGSLADataLoading || isGSLARecommendationsLoading || isFCMetadataLoading) {
        return (
            <div>
                GSLA Data is loading please wait a few seconds. The page will automatically load when it is ready.
                <KatProgress indeterminate></KatProgress>
            </div>
        );
    }

    // Main view for all users
    return (
        <React.Fragment>
            <KatLayout>
                <div slot='main'>
                    <h3>Granular SLA Data for: {siteId}</h3>
                    
                    {/* Success Notification */}
                    {showSuccessNotification && (
                        <div style={{ 
                            marginBottom: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '4px',
                            color: '#155724',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <span style={{ fontWeight: 500 }}>{successMessage}</span>
                            <button 
                                onClick={() => setShowSuccessNotification(false)}
                                style={{ 
                                    marginLeft: '16px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    color: '#155724',
                                    padding: '0 4px'
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}
                    
                    {/* Only show toggle if user has edit permissions */}
                    {hasEditPermission && (
                        <div style={{ 
                            padding: '16px', 
                            backgroundColor: '#f5f5f5', 
                            borderBottom: '1px solid #ddd',
                            marginBottom: '16px' 
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input 
                                    type="checkbox" 
                                    checked={viewOnlyMode} 
                                    onChange={toggleViewMode}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontWeight: 500 }}>View-Only Mode</span>
                            </label>
                        </div>
                    )}
                    
                    <GSLAHierarchicalTableAdapter
                        warehouseId={siteId || ''}
                        gslaData={gslaData}
                        gslaRecommendations={gslaRecommendations}
                        isLoading={isGSLADataLoading || isGSLARecommendationsLoading}
                        userCanEdit={userCanEdit}
                        onSaveSuccess={handleSaveSuccess}
                        availableTiers={availableTiers}
                    />
                </div>
            </KatLayout>
        </React.Fragment>
    );
};

export default ProductionCapabilityManagerWithHierarchy;
